import React, { useState } from "react";
import { Download, Plus, Ban } from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/common/StatusChip";
import { PaymentDialog } from "@/features/finance/PaymentDialog";
import { VoidDialog } from "@/features/finance/VoidDialog";

const SEMAFORO_TONE = { rojo: "red", ambar: "amber", verde: "green", gris: "gray" };

/** Estado de cuenta del sub embebido en su ficha: saldo, órdenes pendientes, pagos y anticipos. */
export const SubFinanceTab = ({ subId, finanzas }) => {
  const { t } = useT();
  const { can } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const { balance, ordenes_pendientes: pendientes, proyectos_pendientes: pendientesProyectos = [], pagos, anticipos } = finanzas;

  const downloadStatement = async () => {
    const res = await api.get(`/payments/sub/${subId}/statement.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "estado_cuenta.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="sub-finance-tab" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-4 text-[12.5px]">
          <span><span className="text-ink-tertiary">{t("finance.payments.approved")}:</span> <b className="tabular-nums">{formatMoney(balance.aprobado_cents)}</b></span>
          <span><span className="text-ink-tertiary">{t("finance.payments.paid")}:</span> <b className="tabular-nums">{formatMoney(balance.pagado_cents)}</b></span>
          <span><span className="text-ink-tertiary">{t("finance.payments.advances_available")}:</span> <b className="tabular-nums">{formatMoney(balance.anticipos_disponibles_cents)}</b></span>
        </div>
        <Button variant="outline" size="sm" data-testid="sub-statement-pdf-button" onClick={downloadStatement}>
          <Download className="mr-1.5 h-3.5 w-3.5" />{t("finance.payments.statement_pdf")}
        </Button>
      </div>

      <div className="rounded-[10px] border border-ink bg-ink p-4">
        <p className="text-[11.5px] text-ink-tertiary">{t("finance.payments.balance")}</p>
        <p data-testid="sub-finance-balance" className="text-[26px] font-semibold tabular-nums text-white">{formatMoney(balance.saldo_cents)}</p>
      </div>

      {can("finance", "create") && (
        <div className="flex gap-2">
          <Button data-testid="sub-new-payment-button" size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => setDialog("payment")}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />{t("finance.payments.new_payment")}
          </Button>
          <Button data-testid="sub-new-advance-button" size="sm" variant="outline" onClick={() => setDialog("advance")}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />{t("finance.payments.new_advance")}
          </Button>
        </div>
      )}

      <div className="rounded-[8px] border border-line bg-white">
        <p className="border-b border-line px-3 py-2 text-[12.5px] font-semibold text-ink">{t("finance.payments.pending_orders")}</p>
        {!pendientes.length ? (
          <p className="p-4 text-center text-[12.5px] text-ink-tertiary">{t("finance.payments.no_pending_orders")}</p>
        ) : (
          <table className="w-full text-[12px]" data-testid="sub-pending-orders-table">
            <tbody>
              {pendientes.map((o) => (
                <tr key={o.work_order_id} className="border-b border-line">
                  <td className="px-3 py-1.5">{o.code} · {o.property_nombre}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(o.pendiente_cents)}</td>
                  <td className="px-3 py-1.5 text-right text-ink-tertiary">{o.antiguedad_dias} d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!!pendientesProyectos.length && (
        <div className="rounded-[8px] border border-line bg-white">
          <p className="border-b border-line px-3 py-2 text-[12.5px] font-semibold text-ink">{t("projects.title")}</p>
          <table className="w-full text-[12px]" data-testid="sub-pending-projects-table">
            <tbody>
              {pendientesProyectos.map((o) => (
                <tr key={o.project_id} className="border-b border-line">
                  <td className="px-3 py-1.5">{o.code} · {o.property_nombre}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(o.pendiente_cents)}</td>
                  <td className="px-3 py-1.5 text-right text-ink-tertiary">{o.antiguedad_dias} d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-[8px] border border-line bg-white">
        <p className="border-b border-line px-3 py-2 text-[12.5px] font-semibold text-ink">{t("finance.payments.history")}</p>
        {!pagos.length ? (
          <p className="p-4 text-center text-[12.5px] text-ink-tertiary">{t("finance.payments.no_payments")}</p>
        ) : (
          <table className="w-full text-[12px]" data-testid="sub-payments-table">
            <thead>
              <tr className="border-b border-line text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
                <th className="px-3 py-1.5 text-left">{t("finance.payments.date")}</th>
                <th className="px-3 py-1.5 text-left">{t("finance.payments.amount")}</th>
                <th className="px-3 py-1.5 text-left">{t("finance.payments.type")}</th>
                <th className="px-3 py-1.5 text-left">{t("finance.payments.orders_covered")}</th>
                <th className="px-3 py-1.5 text-left">{t("finance.payments.registered_by")}</th>
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {pagos.slice(0, 20).map((p) => (
                <tr key={p.id} className="border-b border-line" data-testid={`sub-payment-row-${p.id}`}>
                  <td className="px-3 py-1.5">{formatDate(p.fecha)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatMoney(p.monto_cents)}</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {p.anulado && <StatusChip tone="red" label={t("finance.payments.voided")} />}
                      {p.es_contraasiento && (
                        <StatusChip testId={`sub-payment-contra-${p.id}`} tone="amber" label={t("finance.payments.reversal")} />
                      )}
                      {t(`finance.payments.types.${p.tipo}`)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-ink-secondary">
                    {p.es_a_cuenta
                      ? t("finance.payments.on_account")
                      : [...(p.work_order_allocations || []), ...(p.project_allocations || [])]
                          .map((a) => a.code).filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-ink-secondary">{p.created_by_nombre || "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    {!p.anulado && !p.contraasiento_de_id && can("finance", "update") && (
                      <button data-testid={`sub-void-payment-${p.id}`} onClick={() => setVoidTarget({ kind: "payment", id: p.id })}
                        className="text-ink-tertiary hover:text-state-critical"><Ban className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {anticipos.length > 0 && (
        <div className="rounded-[8px] border border-line bg-white">
          <p className="border-b border-line px-3 py-2 text-[12.5px] font-semibold text-ink">{t("finance.payments.advances_title")}</p>
          <table className="w-full text-[12px]" data-testid="sub-advances-table">
            <tbody>
              {anticipos.map((a) => (
                <tr key={a.id} className="border-b border-line">
                  <td className="px-3 py-1.5">{formatDate(a.fecha)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatMoney(a.monto_cents)}</td>
                  <td className="px-3 py-1.5">{a.anulado ? <StatusChip tone="red" label="Anulado" /> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PaymentDialog open={dialog === "payment"} onOpenChange={(v) => setDialog(v ? "payment" : null)} kind="payment" subId={subId} />
      <PaymentDialog open={dialog === "advance"} onOpenChange={(v) => setDialog(v ? "advance" : null)} kind="advance" subId={subId} />
      {voidTarget && (
        <VoidDialog open={Boolean(voidTarget)} onOpenChange={(v) => !v && setVoidTarget(null)} kind={voidTarget.kind} id={voidTarget.id} />
      )}
    </div>
  );
};
